import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { BrandAPI } from '@business/api/brand';
import { BrandResponse, BrandId } from '@business/entities/brand';
import { CompanyId } from '@business/entities/companies';

export class BrandBloc extends BaseBloc {
  private readonly brandsRepo = new Repository<BrandResponse>({
    getItemId: brand => brand.id,
  });

  readonly brands$ = this.brandsRepo.items$;
  readonly selectBrand = this.brandsRepo.selectItem;

  onReset() {
    this.brandsRepo.reset();
  }

  fetchBrands = (companyId: CompanyId) => {
    return BrandAPI.fetchBrands(companyId).pipe(this.brandsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_brands_error') }));
  };

  fetchBrand = (id: BrandId) => {
    return BrandAPI.fetchBrand(id).pipe(
      this.brandsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_brands_error') }),
    );
  };

  createBrand = (companyId: CompanyId, brand: Partial<BrandResponse>) => {
    return BrandAPI.createBrand(companyId, brand).pipe(
      this.brandsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_brand'),
      }),
    );
  };

  updateBrand = (id: BrandId, brand: Partial<BrandResponse>) => {
    return BrandAPI.updateBrand(id, brand).pipe(
      this.brandsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_brand'),
      }),
    );
  };

  deleteBrand = (newBrand: BrandId, brandId: BrandId, oldBrandName: string, newBrandName: string) => {
    return showDeleteConfirmation(
      'Delete Brand',
      `Do you really want to remove this brand ? Your Stores, Contacts, Organizations and Tickets will be moved from ${oldBrandName} to ${newBrandName}.`,
    ).pipe(
      switchMap(() => BrandAPI.deleteBrand(newBrand, brandId)),
      this.brandsRepo.ops.removeOne(() => brandId),
      handleMessage({
        type: requestMessage('delete_brand'),
      }),
    );
  };
}
